/* ============================================================================
 * ATP Engine Menu Manager
 * ----------------------------------------------------------------------------
 * A staff-only page (section: atp-menu-manager) for editing WHICH products
 * appear WHERE in the engine mega menu, without touching code.
 *
 *   - Loads a manufacturer's menu JSON straight from the theme's assets.
 *   - Three-pane board mirroring the storefront flyout:
 *       Engine models -> Part types -> Products
 *   - Add / rename / reorder / delete models and part types.
 *   - Add products via Shopify's own predictive search, a pasted product URL,
 *     or a raw handle. Remove and reorder products per part type.
 *   - Every product row hydrates from /products/<handle>.js, so you see the
 *     exact title / price / stock state the storefront menu will show.
 *   - "Validate all" checks every placement in the file and lists dead ones.
 *   - Exports a ready-to-upload atp-engine-menu-<slug>.json (v2 slim schema).
 *
 * Storefront JavaScript cannot write theme files, so publishing is:
 *   edit here -> Export -> upload the file to the theme's assets
 *   (Online Store > Themes > Edit code > Assets, replace the file).
 *
 * Access: the section has an "Access key" setting. The page only unlocks when
 * visited as /pages/<page>?key=<that key> (always unlocked inside the Theme
 * Editor preview). The data involved is the public menu JSON — the key just
 * keeps the tool itself out of casual view.
 * ========================================================================== */
(function () {
  'use strict';

  if (window.__atpMenuManagerLoaded) return;
  window.__atpMenuManagerLoaded = true;

  var MAX_CONCURRENT = 4;               // simultaneous /products/*.js requests
  var PRODUCT_TTL_MS = 15 * 60 * 1000;  // shares the storefront menu's cache
  var SESSION_PREFIX = 'atpemm:v2:';    // same keys the mega menu reads
  var SCHEMA_NOTE = 'placements-only: product tiles are hydrated live from /products/<handle>.js \u2014 edit with the Engine Menu Manager page';

  /* ------------------------------------------------------------------ */
  /* Boot: config + access gate                                          */
  /* ------------------------------------------------------------------ */
  var configNode = document.querySelector('[data-atp-mm-config]');
  if (!configNode) return;

  var config;
  try { config = JSON.parse(configNode.textContent); }
  catch (error) { config = null; }
  if (!config) return;

  var lock = document.querySelector('[data-atp-mm-lock]');
  var lockMessage = document.querySelector('[data-atp-mm-lock-message]');
  var app = document.querySelector('[data-atp-mm-app]');
  if (!app) return;

  function authorized() {
    if (config.designMode) return true;
    var expected = String(config.key || '').trim();
    if (!expected) return false;
    var given = '';
    try { given = new URLSearchParams(window.location.search).get('key') || ''; }
    catch (error) { given = ''; }
    return given === expected;
  }

  if (!authorized()) {
    if (lockMessage) {
      lockMessage.textContent = String(config.key || '').trim()
        ? 'Add ?key=YOUR-ACCESS-KEY to this page\u2019s address to open the manager.'
        : 'No access key is set yet. Open this page in the Theme Editor and set one in the section\u2019s settings.';
    }
    return; // lock screen stays visible, app stays hidden
  }

  if (lock) lock.hidden = true;
  app.hidden = false;

  /* ------------------------------------------------------------------ */
  /* State                                                               */
  /* ------------------------------------------------------------------ */
  var state = {
    slug: '',
    label: '',
    src: '',
    data: null,     // normalized v2 document
    mi: 0,          // selected model index
    ci: 0,          // selected component index
    dirty: false,
    validating: false
  };

  var productCache = new Map();   // handle -> {ok, data|status}
  var productInflight = new Map();
  var hydrateQueue = [];
  var activeFetches = 0;

  /* ------------------------------------------------------------------ */
  /* DOM handles                                                         */
  /* ------------------------------------------------------------------ */
  function q(selector) { return app.querySelector(selector); }

  var el = {
    manufacturers: q('[data-atp-mm-manufacturers]'),
    status: q('[data-atp-mm-status]'),
    validate: q('[data-atp-mm-validate]'),
    exportBtn: q('[data-atp-mm-export]'),
    copyBtn: q('[data-atp-mm-copy]'),
    reload: q('[data-atp-mm-reload]'),
    report: q('[data-atp-mm-report]'),
    empty: q('[data-atp-mm-empty]'),
    board: q('[data-atp-mm-board]'),
    models: q('[data-atp-mm-models]'),
    addModel: q('[data-atp-mm-add-model]'),
    componentsTitle: q('[data-atp-mm-components-title]'),
    components: q('[data-atp-mm-components]'),
    addComponent: q('[data-atp-mm-add-component]'),
    crumb: q('[data-atp-mm-crumb]'),
    viewAll: q('[data-atp-mm-viewall]'),
    search: q('[data-atp-mm-search]'),
    results: q('[data-atp-mm-results]'),
    products: q('[data-atp-mm-products]'),
    count: q('[data-atp-mm-count]')
  };

  /* ------------------------------------------------------------------ */
  /* Small helpers                                                       */
  /* ------------------------------------------------------------------ */
  function create(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  function empty(node) { while (node && node.firstChild) node.removeChild(node.firstChild); }

  function rootUrl() {
    var root = config.rootUrl || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root;
  }

  function handleFromUrl(url) {
    var match = /\/products\/([^\/?#]+)/.exec(String(url || ''));
    if (!match) return '';
    try { return decodeURIComponent(match[1]); } catch (error) { return match[1]; }
  }

  function productJsUrl(handle) {
    return rootUrl() + 'products/' + encodeURIComponent(handle) + '.js';
  }

  function productPageUrl(handle) {
    return rootUrl() + 'products/' + encodeURIComponent(handle);
  }

  function prettyHandle(handle) {
    return String(handle || '').split('-').filter(Boolean)
      .map(function (word) { return word.charAt(0).toUpperCase() + word.slice(1); })
      .join(' ');
  }

  function sizedImage(url, width) {
    if (!url || !/^https?:\/\//i.test(url)) return url || '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /* Money — same behavior as the storefront menu */
  function formatWithDelimiters(cents, precision, thousands, decimal) {
    precision = precision === undefined ? 2 : precision;
    thousands = thousands || ',';
    decimal = decimal || '.';
    var fixed = (Number(cents) / 100).toFixed(precision);
    var parts = fixed.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
    return parts[1] ? whole + decimal + parts[1] : whole;
  }

  function formatMoney(cents) {
    if (cents === null || cents === undefined || cents === '') return '';
    var value = Number(cents);
    if (!isFinite(value)) return '';
    var format = window.theme && window.theme.currency && window.theme.currency.format;
    if (format && format.indexOf('{{') > -1) {
      var match = /\{\{\s*(\w+)\s*\}\}/.exec(format);
      var key = match ? match[1] : 'amount';
      var amount;
      switch (key) {
        case 'amount_no_decimals': amount = formatWithDelimiters(value, 0); break;
        case 'amount_with_comma_separator': amount = formatWithDelimiters(value, 2, '.', ','); break;
        case 'amount_no_decimals_with_comma_separator': amount = formatWithDelimiters(value, 0, '.', ','); break;
        case 'amount_with_apostrophe_separator': amount = formatWithDelimiters(value, 2, "'", '.'); break;
        case 'amount_no_decimals_with_space_separator': amount = formatWithDelimiters(value, 0, ' '); break;
        case 'amount_with_space_separator': amount = formatWithDelimiters(value, 2, ' ', ','); break;
        case 'amount_with_period_and_space_separator': amount = formatWithDelimiters(value, 2, ' ', '.'); break;
        default: amount = formatWithDelimiters(value, 2);
      }
      return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
    }
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(value / 100);
    } catch (error) { return '$' + (value / 100).toFixed(2); }
  }

  /* ------------------------------------------------------------------ */
  /* Live product fetching (shared cache with the storefront menu)       */
  /* ------------------------------------------------------------------ */
  function sessionGet(handle) {
    try {
      var raw = window.sessionStorage.getItem(SESSION_PREFIX + handle);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (!entry || !entry.ts || (Date.now() - entry.ts) > PRODUCT_TTL_MS) return null;
      return entry.d || null;
    } catch (error) { return null; }
  }

  function sessionSet(handle, data) {
    try {
      window.sessionStorage.setItem(SESSION_PREFIX + handle, JSON.stringify({ ts: Date.now(), d: data }));
    } catch (error) { /* fine */ }
  }

  function slimProduct(raw, handle) {
    var compareAt = raw.compare_at_price;
    if (compareAt === null || compareAt === undefined) compareAt = raw.compare_at_price_min;
    return {
      t: raw.title || prettyHandle(handle),
      u: raw.url || productPageUrl(handle),
      p: raw.price,
      cp: compareAt,
      pv: !!raw.price_varies,
      av: raw.available !== false,
      img: raw.featured_image || (raw.images && raw.images[0]) || ''
    };
  }

  function fetchProduct(handle, skipSession) {
    if (!skipSession && productCache.has(handle)) return Promise.resolve(productCache.get(handle));

    if (!skipSession) {
      var fromSession = sessionGet(handle);
      if (fromSession) {
        var hit = { ok: true, data: fromSession };
        productCache.set(handle, hit);
        return Promise.resolve(hit);
      }
    }

    if (productInflight.has(handle)) return productInflight.get(handle);

    var request = fetch(productJsUrl(handle), { credentials: 'same-origin' })
      .then(function (response) {
        if (response.status === 404 || response.status === 410) {
          var missing = { ok: false, status: 404 };
          productCache.set(handle, missing);
          return missing;
        }
        if (!response.ok) return { ok: false, status: response.status };
        return response.json().then(function (raw) {
          var result = { ok: true, data: slimProduct(raw, handle) };
          productCache.set(handle, result);
          sessionSet(handle, result.data);
          return result;
        });
      })
      .catch(function () { return { ok: false, status: 0 }; })
      .then(function (result) {
        productInflight.delete(handle);
        return result;
      });

    productInflight.set(handle, request);
    return request;
  }

  function queueHydration(row, handle) {
    var cached = productCache.get(handle);
    if (cached) { applyRowResult(row, handle, cached); return; }
    hydrateQueue.push({ row: row, handle: handle });
    pumpQueue();
  }

  function pumpQueue() {
    while (activeFetches < MAX_CONCURRENT && hydrateQueue.length) {
      var job = hydrateQueue.shift();
      if (!job.row.isConnected) continue;
      activeFetches++;
      (function (currentJob) {
        fetchProduct(currentJob.handle).then(function (result) {
          applyRowResult(currentJob.row, currentJob.handle, result);
        }).catch(function () {
          applyRowResult(currentJob.row, currentJob.handle, { ok: false, status: 0 });
        }).then(function () {
          activeFetches--;
          pumpQueue();
        });
      })(job);
    }
  }

  function applyRowResult(row, handle, result) {
    if (!row.isConnected) return;
    var img = row.querySelector('[data-mm-thumb]');
    var title = row.querySelector('[data-mm-title]');
    var meta = row.querySelector('[data-mm-meta]');
    row.classList.remove('is-loading');

    if (result.ok) {
      var data = result.data;
      if (title) {
        title.textContent = data.t;
        title.href = data.u;
      }
      if (img && data.img) img.src = sizedImage(data.img, 96);
      if (meta) {
        empty(meta);
        var price = formatMoney(data.p);
        if (price) meta.appendChild(create('span', 'atp-mm-price', data.pv ? 'From ' + price : price));
        meta.appendChild(create('span', 'atp-mm-chip ' + (data.av ? 'atp-mm-chip--ok' : 'atp-mm-chip--warn'),
          data.av ? 'In stock' : 'Sold out'));
      }
      return;
    }

    row.classList.add('is-broken');
    if (meta) {
      empty(meta);
      meta.appendChild(create('span', 'atp-mm-chip atp-mm-chip--bad',
        result.status === 404 ? 'Not found \u2014 remove or re-add' : 'Could not check (network)'));
    }
  }

  /* ------------------------------------------------------------------ */
  /* Data: load + normalize (accepts old v1 snapshots and slim v2)       */
  /* ------------------------------------------------------------------ */
  function normalize(raw, slug, label) {
    var models = (raw && Array.isArray(raw.models)) ? raw.models : [];
    return {
      version: 2,
      schema: SCHEMA_NOTE,
      manufacturer: (raw && raw.manufacturer) || label || slug,
      slug: (raw && raw.slug) || slug,
      models: models.map(function (model) {
        return {
          title: String(model.title || 'Untitled model'),
          components: (Array.isArray(model.components) ? model.components : []).map(function (component) {
            var out = { title: String(component.title || 'Untitled part type') };
            if (component.viewAllUrl) out.viewAllUrl = String(component.viewAllUrl);
            out.products = (Array.isArray(component.products) ? component.products : [])
              .map(function (product) {
                var handle = product.handle || handleFromUrl(product.url);
                if (!handle) return null;
                var entry = { handle: String(handle) };
                if (product.id !== undefined && product.id !== null) entry.id = String(product.id);
                return entry;
              })
              .filter(Boolean);
            return out;
          })
        };
      })
    };
  }

  function loadManufacturer(button) {
    var slug = button.getAttribute('data-atp-mm-load');
    var label = button.getAttribute('data-label') || slug;
    var src = button.getAttribute('data-src');
    if (!src) return;

    if (state.dirty && !window.confirm('You have unsaved changes for ' + state.label + '. Load ' + label + ' anyway and lose them?')) {
      return;
    }

    setStatus('Loading ' + label + '\u2026', 'busy');
    markActiveManufacturer(slug);
    hideReport();

    var busted = src + (src.indexOf('?') === -1 ? '?' : '&') + 'mmv=' + Date.now();
    fetch(busted, { credentials: 'same-origin', cache: 'no-store' })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (raw) {
        state.slug = slug;
        state.label = label;
        state.src = src;
        state.data = normalize(raw, slug, label);
        state.mi = 0;
        state.ci = 0;
        state.dirty = false;
        if (el.empty) el.empty.hidden = true;
        if (el.board) el.board.hidden = false;
        renderAll();
        setStatus(label + ' loaded \u2014 no unsaved changes', 'ok');
      })
      .catch(function () {
        setStatus('Could not load ' + label + '\u2019s menu file. Refresh and try again.', 'bad');
      });
  }

  function markActiveManufacturer(slug) {
    if (!el.manufacturers) return;
    el.manufacturers.querySelectorAll('[data-atp-mm-load]').forEach(function (button) {
      button.classList.toggle('is-active', button.getAttribute('data-atp-mm-load') === slug);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Status + dirty                                                      */
  /* ------------------------------------------------------------------ */
  function setStatus(message, tone) {
    if (!el.status) return;
    el.status.textContent = message;
    el.status.className = 'atp-mm-status' + (tone ? ' atp-mm-status--' + tone : '');
  }

  function markDirty() {
    state.dirty = true;
    setStatus(state.label + ' \u2014 unsaved changes. Export when you\u2019re done.', 'dirty');
  }

  window.addEventListener('beforeunload', function (event) {
    if (!state.dirty) return;
    event.preventDefault();
    event.returnValue = '';
  });

  /* ------------------------------------------------------------------ */
  /* Selection helpers                                                   */
  /* ------------------------------------------------------------------ */
  function currentModel() {
    if (!state.data || !state.data.models.length) return null;
    if (state.mi >= state.data.models.length) state.mi = 0;
    return state.data.models[state.mi];
  }

  function currentComponent() {
    var model = currentModel();
    if (!model || !model.components.length) return null;
    if (state.ci >= model.components.length) state.ci = 0;
    return model.components[state.ci];
  }

  function countModelProducts(model) {
    return model.components.reduce(function (total, component) {
      return total + component.products.length;
    }, 0);
  }

  /* ------------------------------------------------------------------ */
  /* Rendering                                                           */
  /* ------------------------------------------------------------------ */
  function renderAll() {
    renderModels();
    renderComponents();
    renderProducts();
  }

  function listRow(kind, index, title, count, selected) {
    var row = create('div', 'atp-mm-row' + (selected ? ' is-selected' : ''));
    row.setAttribute('data-mm-' + kind, String(index));

    var main = create('button', 'atp-mm-row-main');
    main.type = 'button';
    main.setAttribute('data-mm-select-' + kind, String(index));
    main.appendChild(create('span', 'atp-mm-row-title', title));
    main.appendChild(create('span', 'atp-mm-count', String(count)));
    row.appendChild(main);

    var actions = create('div', 'atp-mm-row-actions');
    actions.appendChild(iconButton('\u2191', 'Move up', 'data-mm-up-' + kind, index));
    actions.appendChild(iconButton('\u2193', 'Move down', 'data-mm-down-' + kind, index));
    actions.appendChild(iconButton('\u270e', 'Rename', 'data-mm-rename-' + kind, index));
    actions.appendChild(iconButton('\u00d7', 'Delete', 'data-mm-delete-' + kind, index, 'atp-mm-icon--danger'));
    row.appendChild(actions);
    return row;
  }

  function iconButton(glyph, label, attr, index, extraClass) {
    var button = create('button', 'atp-mm-icon' + (extraClass ? ' ' + extraClass : ''), glyph);
    button.type = 'button';
    button.title = label;
    button.setAttribute('aria-label', label);
    button.setAttribute(attr, String(index));
    return button;
  }

  function renderModels() {
    if (!el.models || !state.data) return;
    empty(el.models);
    state.data.models.forEach(function (model, index) {
      el.models.appendChild(listRow('model', index, model.title, countModelProducts(model), index === state.mi));
    });
    if (!state.data.models.length) {
      el.models.appendChild(create('p', 'atp-mm-blank', 'No engine models yet \u2014 add the first one below.'));
    }
  }

  function renderComponents() {
    var model = currentModel();
    if (el.componentsTitle) el.componentsTitle.textContent = model ? model.title : 'Part types';
    if (!el.components) return;
    empty(el.components);
    if (!model) {
      el.components.appendChild(create('p', 'atp-mm-blank', 'Pick or add an engine model first.'));
      return;
    }
    model.components.forEach(function (component, index) {
      el.components.appendChild(listRow('component', index, component.title, component.products.length, index === state.ci));
    });
    if (!model.components.length) {
      el.components.appendChild(create('p', 'atp-mm-blank', 'No part types in this model yet.'));
    }
  }

  function renderProducts() {
    var model = currentModel();
    var component = currentComponent();

    if (el.crumb) {
      el.crumb.textContent = (state.data ? state.data.manufacturer : '') +
        (model ? ' / ' + model.title : '') + (component ? ' / ' + component.title : '');
    }
    if (el.viewAll) {
      el.viewAll.value = component && component.viewAllUrl ? component.viewAllUrl : '';
      el.viewAll.disabled = !component;
    }
    if (el.search) el.search.disabled = !component;
    if (el.count) el.count.textContent = component ? component.products.length + ' product' + (component.products.length === 1 ? '' : 's') : '';

    if (!el.products) return;
    empty(el.products);
    closeResults();

    if (!component) {
      el.products.appendChild(create('p', 'atp-mm-blank', 'Pick or add a part type to manage its products.'));
      return;
    }

    if (!component.products.length) {
      el.products.appendChild(create('p', 'atp-mm-blank', 'No products yet \u2014 search above to add the first one.'));
      return;
    }

    component.products.forEach(function (placement, index) {
      el.products.appendChild(productRow(placement, index));
    });
  }

  function productRow(placement, index) {
    var row = create('div', 'atp-mm-product is-loading');
    row.setAttribute('data-mm-product', String(index));
    row.setAttribute('data-mm-handle', placement.handle);

    var img = create('img', 'atp-mm-thumb');
    img.setAttribute('data-mm-thumb', '');
    img.alt = '';
    img.loading = 'lazy';
    img.decoding = 'async';
    row.appendChild(img);

    var body = create('div', 'atp-mm-product-body');
    var title = create('a', 'atp-mm-product-title', prettyHandle(placement.handle));
    title.setAttribute('data-mm-title', '');
    title.href = productPageUrl(placement.handle);
    title.target = '_blank';
    title.rel = 'noopener';
    body.appendChild(title);
    body.appendChild(create('span', 'atp-mm-handle', placement.handle));
    var meta = create('div', 'atp-mm-product-meta');
    meta.setAttribute('data-mm-meta', '');
    meta.appendChild(create('span', 'atp-mm-chip', 'Checking\u2026'));
    body.appendChild(meta);
    row.appendChild(body);

    var actions = create('div', 'atp-mm-row-actions');
    actions.appendChild(iconButton('\u2191', 'Move up', 'data-mm-up-product', index));
    actions.appendChild(iconButton('\u2193', 'Move down', 'data-mm-down-product', index));
    actions.appendChild(iconButton('\u00d7', 'Remove from menu', 'data-mm-delete-product', index, 'atp-mm-icon--danger'));
    row.appendChild(actions);

    queueHydration(row, placement.handle);
    return row;
  }

  /* ------------------------------------------------------------------ */
  /* Mutations                                                           */
  /* ------------------------------------------------------------------ */
  function move(list, index, delta) {
    var next = index + delta;
    if (next < 0 || next >= list.length) return false;
    var item = list.splice(index, 1)[0];
    list.splice(next, 0, item);
    return true;
  }

  function inlineRename(row, currentTitle, onCommit) {
    var main = row.querySelector('.atp-mm-row-main');
    if (!main) return;
    main.hidden = true;
    var form = create('div', 'atp-mm-inline-edit');
    var input = create('input');
    input.type = 'text';
    input.value = currentTitle;
    input.className = 'atp-mm-input';
    var save = create('button', 'atp-mm-btn atp-mm-btn--small', 'Save');
    save.type = 'button';
    var cancel = create('button', 'atp-mm-btn atp-mm-btn--small atp-mm-btn--ghost', 'Cancel');
    cancel.type = 'button';
    form.appendChild(input);
    form.appendChild(save);
    form.appendChild(cancel);
    row.insertBefore(form, main);
    input.focus();
    input.select();

    function finish(commit) {
      var value = input.value.trim();
      form.remove();
      main.hidden = false;
      if (commit && value && value !== currentTitle) onCommit(value);
    }
    save.addEventListener('click', function () { finish(true); });
    cancel.addEventListener('click', function () { finish(false); });
    input.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') { event.preventDefault(); finish(true); }
      if (event.key === 'Escape') { event.preventDefault(); finish(false); }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Product search (predictive search) + add-by-handle                  */
  /* ------------------------------------------------------------------ */
  var searchTimer = null;
  var resultsIndex = -1;

  function suggestJsonUrl(query) {
    var base = String(config.suggestUrl || '/search/suggest');
    if (base.indexOf('.json') === -1) base += '.json';
    return base +
      '?q=' + encodeURIComponent(query) +
      '&resources[type]=product' +
      '&resources[limit]=10' +
      '&resources[options][unavailable_products]=show' +
      '&resources[options][fields]=title,product_type,variants.sku,vendor,handle';
  }

  function runSearch(query) {
    var component = currentComponent();
    if (!component) return;
    query = query.trim();
    if (!query) { closeResults(); return; }

    /* A pasted URL or bare handle gets a direct "verify & add" path. */
    var direct = '';
    if (query.indexOf('/products/') > -1) direct = handleFromUrl(query);
    else if (/^[a-z0-9][a-z0-9\-\u00ae%\.]*$/i.test(query) && query.indexOf(' ') === -1) direct = query.toLowerCase();

    fetch(suggestJsonUrl(query), { credentials: 'same-origin' })
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (payload) {
        var products = (payload && payload.resources && payload.resources.results && payload.resources.results.products) || [];
        showResults(products, direct, query);
      })
      .catch(function () { showResults([], direct, query); });
  }

  function showResults(products, directHandle, query) {
    if (!el.results) return;
    empty(el.results);
    resultsIndex = -1;

    var component = currentComponent();
    var existing = {};
    if (component) component.products.forEach(function (p) { existing[p.handle] = true; });

    products.forEach(function (product) {
      var handle = product.handle || handleFromUrl(product.url);
      if (!handle) return;
      var item = create('button', 'atp-mm-result');
      item.type = 'button';
      item.setAttribute('data-mm-add-handle', handle);
      if (product.image) {
        var thumb = create('img', 'atp-mm-result-thumb');
        thumb.src = sizedImage(product.image, 64);
        thumb.alt = '';
        item.appendChild(thumb);
      }
      var text = create('span', 'atp-mm-result-text');
      text.appendChild(create('span', 'atp-mm-result-title', product.title || prettyHandle(handle)));
      text.appendChild(create('span', 'atp-mm-handle', handle));
      item.appendChild(text);
      if (existing[handle]) item.appendChild(create('span', 'atp-mm-chip', 'Already in this part type'));
      el.results.appendChild(item);
    });

    if (directHandle) {
      var verify = create('button', 'atp-mm-result atp-mm-result--verify');
      verify.type = 'button';
      verify.setAttribute('data-mm-add-handle', directHandle);
      var verifyText = create('span', 'atp-mm-result-text');
      verifyText.appendChild(create('span', 'atp-mm-result-title', 'Add by handle: ' + directHandle));
      verifyText.appendChild(create('span', 'atp-mm-handle', 'Will be verified against the live store first'));
      verify.appendChild(verifyText);
      el.results.appendChild(verify);
    }

    if (!el.results.childNodes.length) {
      el.results.appendChild(create('div', 'atp-mm-blank', 'No products found for \u201c' + query + '\u201d.'));
    }
    el.results.hidden = false;
  }

  function closeResults() {
    if (!el.results) return;
    el.results.hidden = true;
    empty(el.results);
    resultsIndex = -1;
  }

  function addHandle(handle) {
    var component = currentComponent();
    if (!component || !handle) return;

    var existingIndex = -1;
    component.products.forEach(function (p, i) { if (p.handle === handle) existingIndex = i; });
    if (existingIndex > -1) {
      closeResults();
      if (el.search) el.search.value = '';
      flashProductRow(existingIndex);
      return;
    }

    setStatus('Checking ' + handle + '\u2026', 'busy');
    fetchProduct(handle).then(function (result) {
      if (!result.ok) {
        setStatus(result.status === 404
          ? 'No live product found at \u201c' + handle + '\u201d \u2014 nothing was added.'
          : 'Could not verify \u201c' + handle + '\u201d (network). Try again.', 'bad');
        return;
      }
      component.products.push({ handle: handle });
      markDirty();
      if (el.search) el.search.value = '';
      closeResults();
      renderModels();      // counts changed
      renderComponents();
      renderProducts();
      flashProductRow(component.products.length - 1);
    });
  }

  function flashProductRow(index) {
    var row = el.products && el.products.querySelector('[data-mm-product="' + index + '"]');
    if (!row) return;
    row.classList.add('is-flash');
    row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    setTimeout(function () { row.classList.remove('is-flash'); }, 1200);
  }

  /* ------------------------------------------------------------------ */
  /* Validate all placements                                             */
  /* ------------------------------------------------------------------ */
  function validateAll() {
    if (!state.data || state.validating) return;
    var jobs = [];
    state.data.models.forEach(function (model, mi) {
      model.components.forEach(function (component, ci) {
        component.products.forEach(function (placement) {
          jobs.push({ handle: placement.handle, path: model.title + ' \u2192 ' + component.title, mi: mi, ci: ci });
        });
      });
    });

    if (!jobs.length) { setStatus('Nothing to validate \u2014 this file has no products.', 'ok'); return; }

    state.validating = true;
    if (el.validate) el.validate.disabled = true;
    showReport();
    var reportList = el.report.querySelector('[data-mm-report-list]');
    var reportHead = el.report.querySelector('[data-mm-report-head]');
    empty(reportList);

    var done = 0;
    var broken = [];
    var cursor = 0;

    function next() {
      if (cursor >= jobs.length) return;
      var job = jobs[cursor++];
      /* skipSession=true: validation should hit the live store, not a 15-min-old cache */
      fetchProduct(job.handle, true).then(function (result) {
        done++;
        if (!result.ok && result.status === 404) broken.push(job);
        if (reportHead) reportHead.textContent = 'Checked ' + done + ' of ' + jobs.length + ' placements \u2014 ' + broken.length + ' problem' + (broken.length === 1 ? '' : 's') + ' found';
        if (done === jobs.length) finishValidation(broken, jobs.length);
        else next();
      });
    }

    if (reportHead) reportHead.textContent = 'Checking ' + jobs.length + ' placements\u2026';
    for (var i = 0; i < MAX_CONCURRENT; i++) next();
  }

  function finishValidation(broken, total) {
    state.validating = false;
    if (el.validate) el.validate.disabled = false;
    var reportList = el.report.querySelector('[data-mm-report-list]');
    var reportHead = el.report.querySelector('[data-mm-report-head]');

    if (!broken.length) {
      if (reportHead) reportHead.textContent = 'All ' + total + ' placements point at live products. \u2713';
      setStatus('Validation finished \u2014 no problems found.', 'ok');
      return;
    }

    if (reportHead) reportHead.textContent = broken.length + ' of ' + total + ' placements point at products that no longer exist (deleted, unpublished, or renamed handle). The storefront menu hides these automatically \u2014 click one to jump there and remove or replace it:';
    broken.forEach(function (job) {
      var item = create('button', 'atp-mm-report-item');
      item.type = 'button';
      item.setAttribute('data-mm-jump-mi', String(job.mi));
      item.setAttribute('data-mm-jump-ci', String(job.ci));
      item.appendChild(create('span', 'atp-mm-handle', job.handle));
      item.appendChild(create('span', '', job.path));
      reportList.appendChild(item);
    });
    setStatus('Validation finished \u2014 ' + broken.length + ' broken placement' + (broken.length === 1 ? '' : 's') + ' listed below the toolbar.', 'bad');
  }

  function showReport() { if (el.report) el.report.hidden = false; }
  function hideReport() {
    if (!el.report) return;
    el.report.hidden = true;
    var list = el.report.querySelector('[data-mm-report-list]');
    if (list) empty(list);
  }

  /* ------------------------------------------------------------------ */
  /* Export / copy / reload                                              */
  /* ------------------------------------------------------------------ */
  function exportJson() {
    if (!state.data) return '';
    return JSON.stringify(state.data, null, 2) + '\n';
  }

  function downloadExport() {
    if (!state.data) return;
    var filename = 'atp-engine-menu-' + state.slug + '.json';
    var blob = new Blob([exportJson()], { type: 'application/json' });
    var url = URL.createObjectURL(blob);
    var link = create('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(function () { URL.revokeObjectURL(url); }, 4000);
    state.dirty = false;
    setStatus('Exported ' + filename + ' \u2014 upload it to the theme\u2019s Assets folder to publish (steps below).', 'ok');
  }

  function copyExport() {
    if (!state.data) return;
    var text = exportJson();
    var finish = function (ok) {
      setStatus(ok
        ? 'JSON copied \u2014 paste it over assets/atp-engine-menu-' + state.slug + '.json in the code editor.'
        : 'Copy failed \u2014 use Export instead.', ok ? 'ok' : 'bad');
      if (ok) state.dirty = false;
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(function () { finish(true); }, function () { finish(false); });
    } else {
      var area = create('textarea');
      area.value = text;
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      var ok = false;
      try { ok = document.execCommand('copy'); } catch (error) { ok = false; }
      area.remove();
      finish(ok);
    }
  }

  function reloadFromTheme() {
    if (!state.slug) return;
    var button = el.manufacturers && el.manufacturers.querySelector('[data-atp-mm-load="' + state.slug + '"]');
    if (button) loadManufacturer(button); // includes its own dirty confirm
  }

  /* ------------------------------------------------------------------ */
  /* Events                                                              */
  /* ------------------------------------------------------------------ */
  app.addEventListener('click', function (event) {
    var target = event.target;

    var load = target.closest('[data-atp-mm-load]');
    if (load) { loadManufacturer(load); return; }

    if (target.closest('[data-atp-mm-validate]')) { validateAll(); return; }
    if (target.closest('[data-atp-mm-export]')) { downloadExport(); return; }
    if (target.closest('[data-atp-mm-copy]')) { copyExport(); return; }
    if (target.closest('[data-atp-mm-reload]')) { reloadFromTheme(); return; }

    var jump = target.closest('[data-mm-jump-mi]');
    if (jump) {
      state.mi = Number(jump.getAttribute('data-mm-jump-mi')) || 0;
      state.ci = Number(jump.getAttribute('data-mm-jump-ci')) || 0;
      renderAll();
      if (el.board) el.board.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    if (!state.data) return;
    var model = currentModel();
    var component = currentComponent();

    /* ------ model rows ------ */
    var selectModel = target.closest('[data-mm-select-model]');
    if (selectModel) {
      state.mi = Number(selectModel.getAttribute('data-mm-select-model'));
      state.ci = 0;
      renderAll();
      return;
    }
    var upModel = target.closest('[data-mm-up-model]');
    if (upModel) {
      var ui = Number(upModel.getAttribute('data-mm-up-model'));
      if (move(state.data.models, ui, -1)) {
        if (state.mi === ui) state.mi = ui - 1; else if (state.mi === ui - 1) state.mi = ui;
        markDirty(); renderModels();
      }
      return;
    }
    var downModel = target.closest('[data-mm-down-model]');
    if (downModel) {
      var di = Number(downModel.getAttribute('data-mm-down-model'));
      if (move(state.data.models, di, 1)) {
        if (state.mi === di) state.mi = di + 1; else if (state.mi === di + 1) state.mi = di;
        markDirty(); renderModels();
      }
      return;
    }
    var renameModel = target.closest('[data-mm-rename-model]');
    if (renameModel) {
      var rmi = Number(renameModel.getAttribute('data-mm-rename-model'));
      var rmRow = renameModel.closest('.atp-mm-row');
      inlineRename(rmRow, state.data.models[rmi].title, function (value) {
        state.data.models[rmi].title = value;
        markDirty(); renderModels(); renderComponents(); renderProducts();
      });
      return;
    }
    var deleteModel = target.closest('[data-mm-delete-model]');
    if (deleteModel) {
      var dmi = Number(deleteModel.getAttribute('data-mm-delete-model'));
      var dm = state.data.models[dmi];
      if (window.confirm('Delete engine model \u201c' + dm.title + '\u201d and its ' + countModelProducts(dm) + ' product placement(s) from the menu? (Products themselves are not touched.)')) {
        state.data.models.splice(dmi, 1);
        if (state.mi >= state.data.models.length) state.mi = Math.max(0, state.data.models.length - 1);
        state.ci = 0;
        markDirty(); renderAll();
      }
      return;
    }

    /* ------ component rows ------ */
    var selectComponent = target.closest('[data-mm-select-component]');
    if (selectComponent) {
      state.ci = Number(selectComponent.getAttribute('data-mm-select-component'));
      renderComponents(); renderProducts();
      return;
    }
    var upComponent = target.closest('[data-mm-up-component]');
    if (upComponent && model) {
      var uci = Number(upComponent.getAttribute('data-mm-up-component'));
      if (move(model.components, uci, -1)) {
        if (state.ci === uci) state.ci = uci - 1; else if (state.ci === uci - 1) state.ci = uci;
        markDirty(); renderComponents();
      }
      return;
    }
    var downComponent = target.closest('[data-mm-down-component]');
    if (downComponent && model) {
      var dci = Number(downComponent.getAttribute('data-mm-down-component'));
      if (move(model.components, dci, 1)) {
        if (state.ci === dci) state.ci = dci + 1; else if (state.ci === dci + 1) state.ci = dci;
        markDirty(); renderComponents();
      }
      return;
    }
    var renameComponent = target.closest('[data-mm-rename-component]');
    if (renameComponent && model) {
      var rci = Number(renameComponent.getAttribute('data-mm-rename-component'));
      var rcRow = renameComponent.closest('.atp-mm-row');
      inlineRename(rcRow, model.components[rci].title, function (value) {
        model.components[rci].title = value;
        markDirty(); renderComponents(); renderProducts();
      });
      return;
    }
    var deleteComponent = target.closest('[data-mm-delete-component]');
    if (deleteComponent && model) {
      var dcIndex = Number(deleteComponent.getAttribute('data-mm-delete-component'));
      var dc = model.components[dcIndex];
      if (window.confirm('Delete part type \u201c' + dc.title + '\u201d and its ' + dc.products.length + ' product placement(s)? (Products themselves are not touched.)')) {
        model.components.splice(dcIndex, 1);
        if (state.ci >= model.components.length) state.ci = Math.max(0, model.components.length - 1);
        markDirty(); renderModels(); renderComponents(); renderProducts();
      }
      return;
    }

    /* ------ product rows ------ */
    var addHandleButton = target.closest('[data-mm-add-handle]');
    if (addHandleButton) { addHandle(addHandleButton.getAttribute('data-mm-add-handle')); return; }

    var upProduct = target.closest('[data-mm-up-product]');
    if (upProduct && component) {
      if (move(component.products, Number(upProduct.getAttribute('data-mm-up-product')), -1)) {
        markDirty(); renderProducts();
      }
      return;
    }
    var downProduct = target.closest('[data-mm-down-product]');
    if (downProduct && component) {
      if (move(component.products, Number(downProduct.getAttribute('data-mm-down-product')), 1)) {
        markDirty(); renderProducts();
      }
      return;
    }
    var deleteProduct = target.closest('[data-mm-delete-product]');
    if (deleteProduct && component) {
      component.products.splice(Number(deleteProduct.getAttribute('data-mm-delete-product')), 1);
      markDirty(); renderModels(); renderComponents(); renderProducts();
      return;
    }

    /* clicking anywhere outside the search area closes the results list */
    if (!target.closest('.atp-mm-search-wrap')) closeResults();
  });

  /* add-model / add-component forms (submit on Enter or button) */
  function bindAddForm(container, onAdd) {
    if (!container) return;
    var input = container.querySelector('input');
    var button = container.querySelector('button');
    function commit() {
      var value = (input.value || '').trim();
      if (!value) return;
      onAdd(value);
      input.value = '';
      input.focus();
    }
    if (button) button.addEventListener('click', commit);
    if (input) input.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') { event.preventDefault(); commit(); }
    });
  }

  bindAddForm(el.addModel, function (value) {
    if (!state.data) return;
    state.data.models.push({ title: value, components: [] });
    state.mi = state.data.models.length - 1;
    state.ci = 0;
    markDirty(); renderAll();
  });

  bindAddForm(el.addComponent, function (value) {
    var model = currentModel();
    if (!model) return;
    model.components.push({ title: value, products: [] });
    state.ci = model.components.length - 1;
    markDirty(); renderModels(); renderComponents(); renderProducts();
  });

  /* view-all URL input */
  if (el.viewAll) {
    el.viewAll.addEventListener('change', function () {
      var component = currentComponent();
      if (!component) return;
      var value = el.viewAll.value.trim();
      if (value) component.viewAllUrl = value;
      else delete component.viewAllUrl;
      markDirty();
    });
  }

  /* search input: debounce + keyboard navigation of results */
  if (el.search) {
    el.search.addEventListener('input', function () {
      window.clearTimeout(searchTimer);
      var value = el.search.value;
      searchTimer = window.setTimeout(function () { runSearch(value); }, 250);
    });
    el.search.addEventListener('keydown', function (event) {
      if (!el.results || el.results.hidden) {
        if (event.key === 'Enter') event.preventDefault(); // never submit anything
        return;
      }
      var items = Array.prototype.slice.call(el.results.querySelectorAll('[data-mm-add-handle]'));
      if (!items.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        resultsIndex = event.key === 'ArrowDown'
          ? (resultsIndex + 1) % items.length
          : (resultsIndex - 1 + items.length) % items.length;
        items.forEach(function (item, index) { item.classList.toggle('is-highlight', index === resultsIndex); });
        items[resultsIndex].scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (resultsIndex > -1 && items[resultsIndex]) items[resultsIndex].click();
      } else if (event.key === 'Escape') {
        closeResults();
      }
    });
  }

  setStatus('Pick a manufacturer above to load its menu.', '');
})();
